import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageHeader, LangSwitch } from "@/components/common";
import { Field } from "@/app/tool/Options";

function Section({ title, children, danger }) {
  return <section className={`panel p-6 space-y-4 ${danger ? "border-red-900/70" : ""}`}><h2 className={`font-semibold ${danger ? "text-red-400" : ""}`}>{title}</h2>{children}</section>;
}

export default function Settings() {
  const { t, errMsg } = useI18n();
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(user.name || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [confirm, setConfirm] = useState("");
  const run = async (fn, ok = true) => { try { await fn(); if (ok) toast.success(t("settings.saved")); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <PageHeader title={t("settings.title")} />
      <Section title={t("settings.profile")}>
        <Field label={t("common.email")}><Input value={user.email} disabled data-testid="settings-email" /></Field>
        <p className="text-xs" data-testid="settings-email-verified">
          <span className={user.email_verified ? "text-emerald-400" : "text-amber-300"}>{user.email_verified ? t("auth.verified") : t("auth.notVerified")}</span>
        </p>
        <Field label={t("common.name")}><Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" /></Field>
        <p className="text-xs text-neutral-500">{t("settings.providers")}: {(user.auth_providers || []).join(", ")}</p>
        <button onClick={() => run(async () => setUser((await api.patch("/auth/profile", { name })).data))} className="btn-brand rounded-full px-5 py-2 text-sm font-semibold" data-testid="settings-save-profile-btn">{t("common.save")}</button>
      </Section>
      <Section title={t("settings.language")}>
        <LangSwitch onChange={(l) => run(async () => setUser((await api.patch("/auth/profile", { lang: l })).data), false)} />
      </Section>
      <Section title={user.has_password ? t("settings.changePw") : t("settings.setPw")}>
        {user.has_password && <Field label={t("settings.currentPw")}><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="settings-current-pw-input" /></Field>}
        <Field label={t("auth.newPassword")}><Input type="password" minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="settings-new-pw-input" /></Field>
        <button disabled={pw.new_password.length < 8} onClick={() => run(async () => { await api.post("/auth/change-password", pw); setPw({ current_password: "", new_password: "" }); setUser({ ...user, has_password: true }); })}
          className="btn-brand rounded-full px-5 py-2 text-sm font-semibold disabled:opacity-50" data-testid="settings-change-pw-btn">{t("common.save")}</button>
      </Section>
      <Section title={t("settings.danger")} danger>
        <p className="text-sm text-neutral-400">{t("settings.dangerText")}</p>
        <Field label={t("settings.typeEmail")}><Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="delete-confirm-input" /></Field>
        <button disabled={confirm !== user.email} onClick={() => run(async () => { await api.post("/auth/delete-account", { confirm }); setUser(false); nav("/"); }, false)}
          className="rounded-full bg-red-600 hover:bg-red-700 text-white px-5 py-2 text-sm font-semibold disabled:opacity-40" data-testid="delete-account-btn">{t("settings.deleteBtn")}</button>
      </Section>
    </div>
  );
}
